import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, CollectionRow, EventRow, PackageRow } from '../api';
import { eventEndpoints, opponentEndpoints, packageEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Field, Panel } from '../ui';
import { INPUT } from './event/shared';
import { GroupSeriesPanel, MakeSeriesPanel, SeriesPackageForm } from './series/SeasonSeries';

/**
 * Series, in one place (D-324).
 *
 * A series could only be made from inside one event's Collections tab, so there
 * was nowhere to see them all, describe one, or tell which packages were sold
 * with it. This is that place: each series with its web address, its
 * description (which is what its page says under the name), the events in it,
 * and the packages its page leads with.
 *
 * Events join a series from their own Collections tab, and packages from the
 * package form. Both are listed here rather than edited, so each fact still has
 * one screen that changes it.
 *
 * Since D-375 a series is usually a run of games against one team in one
 * season ("vs Quincy · Jul 1–3"): it names its opponent and season, its dates
 * are its games' dates, and choosing a season here offers **Group games into
 * series**, **Make a series**, and on each series **Create a package**.
 */
export default function SeriesPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [seasonId, setSeasonId] = useState('');

  const seasons = useQuery({ queryKey: ['collections', 'seasons'], queryFn: () => eventEndpoints.seasons() });
  const series = useQuery({ queryKey: ['collections', 'series', 'all', seasonId], queryFn: () => eventEndpoints.series(true, seasonId || null) });
  const events = useQuery({ queryKey: ['events'], queryFn: () => eventEndpoints.list() });
  const packages = useQuery({ queryKey: ['packages'], queryFn: () => packageEndpoints.list() });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['collections'] });
    void queryClient.invalidateQueries({ queryKey: ['series'] });
    void queryClient.invalidateQueries({ queryKey: ['events'] });
    void queryClient.invalidateQueries({ queryKey: ['packages'] });
    void queryClient.invalidateQueries({ queryKey: ['season-games'] });
  };
  const onError = (cause: ApiError) => setError(cause.message);

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  const editable = can('events.manage');
  const rows = series.data?.series ?? [];
  const season = (seasons.data?.seasons ?? []).find((row) => row.id === seasonId) ?? null;

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel title="Season" description="Choose a season to see its series in date order and to group its games into series by opponent.">
        <div className="px-5 py-4">
          <Field label="Season" htmlFor="series-season">
            <select id="series-season" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} className={`w-72 ${INPUT}`}>
              <option value="">Every series</option>
              {(seasons.data?.seasons ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Panel>
      {season && editable ? <GroupSeriesPanel season={season} onChanged={refresh} /> : null}
      <Panel
        title="Series"
        description="A name you promote a run of events under, with its own page on the storefront at /series/…. The page leads with the packages sold with the series, then lists every event in it that is still to come. Events join a series on their Collections tab; packages join on the package form."
        actions={
          editable && !adding ? (
            <Button variant="primary" onClick={() => setAdding(true)}>
              New series
            </Button>
          ) : null
        }
      >
        {adding ? (
          <SeriesForm
            series={null}
            seasonId={seasonId || null}
            onDone={() => {
              setAdding(false);
              setError(null);
              refresh();
            }}
            onCancel={() => setAdding(false)}
            onError={onError}
          />
        ) : null}
        {series.isLoading ? <p className="px-5 py-4 text-sm text-ink-500">Loading…</p> : null}
        {!series.isLoading && rows.length === 0 && !adding ? <p className="px-5 py-4 text-sm text-ink-500">No series yet.</p> : null}
        <ul className="divide-y divide-line">
          {rows.map((row) => (
            <SeriesRow
              key={row.id}
              series={row}
              events={(events.data?.events ?? []).filter((event) => event.series_id === row.id)}
              packages={(packages.data?.packages ?? []).filter((pkg) => pkg.series_id === row.id)}
              editable={editable}
              canPackage={can('packages.manage')}
              onSaved={() => {
                setError(null);
                refresh();
              }}
              onError={onError}
            />
          ))}
        </ul>
      </Panel>
      {season && editable ? <MakeSeriesPanel season={season} series={rows} onChanged={refresh} /> : null}
    </div>
  );
}

/** "Jul 1 – Jul 3, 2027", from the series' games (a series stores no dates, D-375). */
function dateRange(first: string | null | undefined, last: string | null | undefined): string | null {
  if (!first || !last) return null;
  const format = (date: string, year: boolean) => new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', ...(year ? { year: 'numeric' } : {}) });
  return first === last ? format(first, true) : `${format(first, false)} – ${format(last, true)}`;
}

function SeriesRow({
  series,
  events,
  packages,
  editable,
  canPackage,
  onSaved,
  onError,
}: {
  series: CollectionRow;
  events: EventRow[];
  packages: PackageRow[];
  editable: boolean;
  canPackage: boolean;
  onSaved: () => void;
  onError: (cause: ApiError) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [packaging, setPackaging] = useState(false);
  const toggle = useMutation({
    mutationFn: () => eventEndpoints.updateSeries(series.id, { active: series.active !== 1 }),
    onSuccess: onSaved,
    onError,
  });
  const onSale = packages.filter((pkg) => pkg.status === 'ON_SALE');

  if (editing) {
    return (
      <li className="px-5 py-4">
        <SeriesForm
          series={series}
          seasonId={series.season_id ?? null}
          onDone={() => {
            setEditing(false);
            onSaved();
          }}
          onCancel={() => setEditing(false)}
          onError={onError}
        />
      </li>
    );
  }

  return (
    <li className="px-5 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-ink">
            {series.name} {series.active === 1 ? null : <Badge>Off</Badge>}
          </p>
          <p className="font-mono text-xs text-ink-400">{series.slug ? `/series/${series.slug}` : 'No web address, so no page'}</p>
          {series.opponent_name || series.season_name || series.first_date ? (
            <p className="mt-0.5 text-sm text-ink-600">
              {[series.opponent_name ? `vs ${series.opponent_name}` : null, dateRange(series.first_date, series.last_date), series.games !== undefined ? `${series.games} ${series.games === 1 ? 'game' : 'games'}` : null, series.season_name]
                .filter(Boolean)
                .join(' · ')}
            </p>
          ) : null}
          {series.description ? <p className="mt-1 max-w-2xl text-sm text-ink-600">{series.description}</p> : null}
        </div>
        {editable ? (
          <div className="flex gap-2">
            {canPackage && (series.games ?? 0) > 1 && packages.length === 0 ? <Button onClick={() => setPackaging((open) => !open)}>{packaging ? 'Close' : 'Create a package for this series'}</Button> : null}
            <Button onClick={() => setEditing(true)}>Edit</Button>
            <Button onClick={() => toggle.mutate()} disabled={toggle.isPending}>
              {series.active === 1 ? 'Switch off' : 'Switch on'}
            </Button>
          </div>
        ) : null}
      </div>
      {packaging ? (
        <SeriesPackageForm
          series={series}
          onDone={() => {
            onSaved();
          }}
        />
      ) : null}
      <div className="mt-3 grid gap-4 text-sm sm:grid-cols-2">
        <div>
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Events ({events.length})</p>
          {events.length === 0 ? (
            <p className="text-ink-500">None yet. Add one from an event&rsquo;s Collections tab.</p>
          ) : (
            <ul className="mt-1 space-y-0.5">
              {events.map((event) => (
                <li key={event.id}>
                  <Link to={`/events/${event.id}`} className="text-brand hover:underline">
                    {event.title}
                  </Link>{' '}
                  <span className="text-xs text-ink-400">{event.status.toLowerCase()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <p className="text-xs font-semibold tracking-wide text-ink-400 uppercase">
            Packages ({packages.length}
            {packages.length > 0 ? `, ${onSale.length} on sale` : ''})
          </p>
          {packages.length === 0 ? (
            <p className="text-ink-500">None. Pick this series on a package&rsquo;s form and its page will lead with it.</p>
          ) : (
            <ul className="mt-1 space-y-0.5">
              {packages.map((pkg) => (
                <li key={pkg.id}>
                  <Link to={`/packages/${pkg.id}`} className="text-brand hover:underline">
                    {pkg.name}
                  </Link>{' '}
                  <span className="text-xs text-ink-400">{pkg.status === 'ON_SALE' ? 'on sale, shown on the page' : `${pkg.status.toLowerCase().replace('_', ' ')}, not shown`}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </li>
  );
}

function SeriesForm({ series, seasonId, onDone, onCancel, onError }: { series: CollectionRow | null; seasonId: string | null; onDone: () => void; onCancel: () => void; onError: (cause: ApiError) => void }) {
  const opponents = useQuery({ queryKey: ['opponents'], queryFn: () => opponentEndpoints.list() });
  const seasons = useQuery({ queryKey: ['collections', 'seasons'], queryFn: () => eventEndpoints.seasons() });
  const [opponentId, setOpponentId] = useState(series?.opponent_id ?? '');
  const [season, setSeason] = useState(series?.season_id ?? seasonId ?? '');
  const [name, setName] = useState(series?.name ?? '');
  const [slug, setSlug] = useState(series?.slug ?? '');
  const [description, setDescription] = useState(series?.description ?? '');
  const save = useMutation({
    mutationFn: () => {
      const input = { name: name.trim(), description: description.trim() || null, opponent_id: opponentId || null, season_id: season || null, ...(slug.trim() ? { slug: slug.trim() } : {}) };
      return series ? eventEndpoints.updateSeries(series.id, input) : eventEndpoints.createSeries(input);
    },
    onSuccess: onDone,
    onError,
  });
  return (
    <form
      className="space-y-3 border-b border-line px-5 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex flex-wrap gap-4">
        <Field label="Name" htmlFor="series-name">
          <input id="series-name" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} className={`w-72 ${INPUT}`} />
        </Field>
        <Field label="Web address" htmlFor="series-slug" hint={series ? 'Changing it breaks links already shared' : 'Made from the name if left empty'}>
          <input
            id="series-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
            placeholder="comedy-nights"
            maxLength={80}
            className={`w-56 font-mono ${INPUT}`}
          />
        </Field>
      </div>
      <div className="flex flex-wrap gap-4">
        <Field label="Opponent" htmlFor="series-opponent" hint="For a run of games against one team">
          <select id="series-opponent" value={opponentId} onChange={(e) => setOpponentId(e.target.value)} className={`w-72 ${INPUT}`}>
            <option value="">None — not a series of games</option>
            {(opponents.data?.opponents ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Season" htmlFor="series-season-pick">
          <select id="series-season-pick" value={season} onChange={(e) => setSeason(e.target.value)} className={`w-56 ${INPUT}`}>
            <option value="">None</option>
            {(seasons.data?.seasons ?? []).map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Description" htmlFor="series-description" hint="Printed under the name on the series page">
        <textarea id="series-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} className={`w-full max-w-2xl ${INPUT}`} />
      </Field>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={save.isPending || !name.trim()}>
          {save.isPending ? 'Saving…' : series ? 'Save' : 'Create series'}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
